import type { RenderElementProps, RenderLeafProps } from 'slate-react';
import type { PegaCustom } from 'slate';
import type { ComponentType, ReactNode, ReactElement } from 'react';
import styled from 'styled-components';

import { Text, Link, Image } from '@pega/cosmos-react-core';

import BlockQuote from '../DecoratorComponents/BlockQuote';
import CodeBlock from '../DecoratorComponents/CodeBlock';
import Code from '../DecoratorComponents/Code';
import HorizontalRule from '../DecoratorComponents/HorizontalRule';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeaderCell,
  TableBody
} from '../DecoratorComponents/Table';

// Renderer for block level elements
export const slateNodeRenderer = ({ children, element, attributes }: RenderElementProps) => {
  switch (element.type) {
    case 'heading-1':
      return (
        <Text variant='h1' {...attributes}>
          {children}
        </Text>
      );
    case 'heading-2':
      return (
        <Text variant='h2' {...attributes}>
          {children}
        </Text>
      );
    case 'heading-3':
      return (
        <Text variant='h3' {...attributes}>
          {children}
        </Text>
      );
    case 'heading-4':
      return (
        <Text variant='h4' {...attributes}>
          {children}
        </Text>
      );
    case 'ordered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'unordered-list':
      return <ul {...attributes}>{children}</ul>;
    case 'list-item':
      return (
        <li
          {...attributes}
          data-order={element.order}
          data-level={element.level}
          data-id={element.id}
          data-parent-id={element.parentId}
        >
          {children}
        </li>
      );
    case 'block-quote':
      return <BlockQuote {...attributes}>{children}</BlockQuote>;
    case 'code-block':
      return (
        <CodeBlock {...attributes} language={element.language}>
          {children}
        </CodeBlock>
      );
    case 'horizontal-rule':
      return <HorizontalRule />;
    case 'custom': {
      const CustomComponent = element.component;
      const contentWithFormatting = {
        ...element.content,
        ...(element.bold && { bold: true }),
        ...(element.italic && { italic: true }),
        ...(element.underline && { underline: true }),
        ...(element.superscript && { superscript: true }),
        ...(element.subscript && { subscript: true }),
        ...(element['line-through'] && { 'line-through': true }),
        ...(element.code && { code: true })
      };
      let child = <CustomComponent {...attributes}>{contentWithFormatting}</CustomComponent>;
      if (element.href) {
        child = <Link href={element.href}>{child}</Link>;
      }
      if (element.italic) {
        child = <em>{child}</em>;
      }
      if (element.underline) {
        child = <u>{child}</u>;
      }
      if (element.superscript) {
        child = <sup>{child}</sup>;
      }
      if (element.subscript) {
        child = <sub>{child}</sub>;
      }
      if (element.bold) {
        child = <strong>{child}</strong>;
      }
      if (element['line-through']) {
        child = <del>{child}</del>;
      }
      if (element.code) {
        child = <Code>{child}</Code>;
      }
      return child;
    }
    case 'override': {
      return element.component;
    }
    case 'table':
      return <Table {...attributes}>{children}</Table>;
    case 'table-head':
      return <TableHead {...attributes}>{children}</TableHead>;
    case 'table-body':
      return <TableBody {...attributes}>{children}</TableBody>;
    case 'table-row':
      return <TableRow {...attributes}>{children}</TableRow>;
    case 'table-cell': {
      const cellProps = element.align ? { ...attributes, align: element.align } : attributes;
      if (element.isHeaderCell) {
        return <TableHeaderCell {...cellProps}>{children}</TableHeaderCell>;
      }
      return <TableCell {...cellProps}>{children}</TableCell>;
    }
    case 'paragraph':
      return (
        <div>
          <Text {...attributes}>{children}</Text>
        </div>
      );
    case 'image':
      return (
        <div {...attributes}>
          <Image src={element.src} alt={element.alt ?? ''} />
          {children}
        </div>
      );
    case 'image-placeholder':
      return <div {...attributes}>{children}</div>;
    default: {
      return <span {...attributes}>{children}</span>;
    }
  }
};

type Node = {
  children: JSX.Element[];
  type: string;
  align?: 'left' | 'center' | 'right';
  href?: string;
  italic?: boolean;
  start?: number;
  bold?: boolean;
  underline?: boolean;
  superscript?: boolean;
  subscript?: boolean;
  'line-through'?: boolean;
  code?: boolean;
  isHeaderCell?: boolean;
  component?:
    | JSX.Element
    | ComponentType<{ children: Record<string, string | boolean> | ReactElement }>;
  src?: string;
  alt?: string;
  content?: Record<string, string>;
};

// Renderer for block level elements
export const nodeRenderer = (node: Node) => {
  switch (node.type) {
    case 'heading-1':
      return <Text variant='h1'>{node.children}</Text>;
    case 'heading-2':
      return <Text variant='h2'>{node.children}</Text>;
    case 'heading-3':
      return <Text variant='h3'>{node.children}</Text>;
    case 'heading-4':
      return <Text variant='h4'>{node.children}</Text>;
    case 'ordered-list':
      return <ol start={node.start}>{node.children}</ol>;
    case 'unordered-list':
      return <ul>{node.children}</ul>;
    case 'list-item':
      return <li>{node.children}</li>;
    case 'block-quote':
      return <BlockQuote>{node.children}</BlockQuote>;
    case 'code-block':
      return <CodeBlock>{node.children}</CodeBlock>;
    case 'horizontal-rule':
      return <HorizontalRule />;
    case 'custom': {
      const CustomComponent = node.component as ComponentType<{
        children?: Record<string, string | boolean> | ReactElement;
      }>;
      const contentWithFormatting = {
        ...node.content,
        ...(node.bold && { bold: true }),
        ...(node.italic && { italic: true }),
        ...(node.underline && { underline: true }),
        ...(node.superscript && { superscript: true }),
        ...(node.subscript && { subscript: true }),
        ...(node['line-through'] && { 'line-through': true }),
        ...(node.code && { code: true })
      };
      let child = <CustomComponent>{contentWithFormatting}</CustomComponent>;
      if (node.href) {
        child = <Link href={node.href}>{child}</Link>;
      }
      if (node.italic) {
        child = <em>{child}</em>;
      }
      if (node.underline) {
        child = <u>{child}</u>;
      }
      if (node.superscript) {
        child = <sup>{child}</sup>;
      }
      if (node.subscript) {
        child = <sub>{child}</sub>;
      }
      if (node.bold) {
        child = <strong>{child}</strong>;
      }
      if (node['line-through']) {
        child = <del>{child}</del>;
      }
      if (node.code) {
        child = <Code>{child}</Code>;
      }
      return child;
    }
    case 'override': {
      return node.component as JSX.Element;
    }
    case 'table':
      return <Table>{node.children}</Table>;
    case 'table-head':
      return <TableHead>{node.children}</TableHead>;
    case 'table-body':
      return <TableBody>{node.children}</TableBody>;
    case 'table-row':
      return <TableRow>{node.children}</TableRow>;
    case 'table-cell': {
      const cellProps = node.align ? { align: node.align } : undefined;
      if (node.isHeaderCell) {
        return <TableHeaderCell {...cellProps}>{node.children}</TableHeaderCell>;
      }
      return <TableCell {...cellProps}>{node.children}</TableCell>;
    }
    case 'paragraph':
      return (
        <div>
          <Text>{node.children}</Text>
        </div>
      );
    case 'image':
      return (
        <div>
          <Image src={node.src} alt={node.alt ?? ''} />
          {node.children}
        </div>
      );
    case 'image-placeholder':
      return <div>{node.children}</div>;
    case 'space':
      return <br />;
    default: {
      return <>{node.children}</>;
    }
  }
};

// Renderer for inline elements
export const slateLeafRenderer = (props: RenderLeafProps) => {
  let child = props.children;

  if (props.leaf.searchResult) {
    const CustomComponent = props.leaf.searchResult.component;
    return (
      <span {...props.attributes}>
        <CustomComponent {...props.leaf.searchResult.props}>{child}</CustomComponent>
      </span>
    );
  }

  if (props.leaf.code) {
    child = <Code>{child}</Code>;
  }

  if (props.leaf['line-through']) {
    child = <del>{child}</del>;
  }

  if (props.leaf.italic) {
    child = <em>{child}</em>;
  }

  if (props.leaf.underline) {
    child = <u>{child}</u>;
  }

  if (props.leaf.superscript) {
    child = <sup>{child}</sup>;
  }

  if (props.leaf.subscript) {
    child = <sub>{child}</sub>;
  }

  if (props.leaf.bold) {
    child = <strong>{child}</strong>;
  }

  if (props.leaf.href) {
    child = <Link href={props.leaf.href}>{child}</Link>;
  }

  return <span {...props.attributes}>{child}</span>;
};

export const StyledSpan = styled.span`
  white-space: pre-line;
`;

export const leafRenderer = (leaf: PegaCustom.Text) => {
  let child: string | ReactNode = leaf.text;
  if (leaf.searchResult) {
    const CustomComponent = leaf.searchResult.component;
    return (
      <span>
        <CustomComponent {...leaf.searchResult.props}>{child}</CustomComponent>
      </span>
    );
  }

  if (leaf.code) {
    child = <Code>{child}</Code>;
  }

  if (leaf['line-through']) {
    child = <del>{child}</del>;
  }

  if (leaf.italic) {
    child = <em>{child}</em>;
  }

  if (leaf.underline) {
    child = <u>{child}</u>;
  }

  if (leaf.superscript) {
    child = <sup>{child}</sup>;
  }

  if (leaf.subscript) {
    child = <sub>{child}</sub>;
  }

  if (leaf.bold) {
    child = <strong>{child}</strong>;
  }

  if (leaf.href) {
    child = <Link href={leaf.href}>{child}</Link>;
  }

  return <StyledSpan>{child}</StyledSpan>;
};
